<template>
  <v-row justify="center" v-if="plan_inscripcion && programa">
    <v-dialog v-model="dialog" persistent scrollable width="1100" :fullscreen="$vuetify.breakpoint.mobile">
      <v-card>
        <!-- Header -->
        <v-toolbar color="blue darken-3" dark dense flat>
          <v-icon class="mr-2">mdi-clipboard-check-outline</v-icon>
          <v-toolbar-title class="font-weight-bold subtitle-1">Aprobación de Plan de Pagos</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip small dark color="blue darken-1" class="font-weight-bold">
            <v-icon small left>mdi-school</v-icon>
            {{ programa.nombre_compuesto }}
          </v-chip>
        </v-toolbar>

        <v-card-text class="pa-4">

          <!-- Alertas y avisos -->
          <v-alert
            dense
            border="left"
            type="warning"
            colored-border
            class="mb-3"
            elevation="1"
          >
            <div class="body-2">
              <strong>NOTA:</strong> Esta ventana aparece una sola vez por estudiante durante el primer pago.
              Usted será responsable de aprobar que el estudiante esté en el plan correcto.
            </div>
          </v-alert>

          <v-alert
            v-if="existRecibo"
            dense
            border="left"
            type="error"
            colored-border
            class="mb-3"
            elevation="1"
          >
            <div class="body-2">
              <strong>AVISO:</strong> Esta inscripción ya tiene recibos generados. Solo queda aprobar el plan.
            </div>
          </v-alert>

          <!-- Información del Programa -->
          <v-card outlined class="mb-4">
            <v-card-title class="py-2 blue darken-3 white--text subtitle-2">
              <v-icon small left color="white">mdi-information-outline</v-icon>
              Información del Programa
            </v-card-title>
            <v-card-text class="py-2">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <span class="caption grey--text text--darken-1">Estado del programa</span>
                  <div>
                    <v-chip
                      small
                      :color="programa.estado_programa ? 'green' : 'grey'"
                      dark
                      class="font-weight-bold"
                    >
                      {{ programa.estado_programa ? programa.estado_programa.nombre : 'Sin estado' }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <span class="caption grey--text text--darken-1">Revisar que la inscripción esté en el plan correcto</span>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Contenido principal en dos columnas -->
          <v-row>
            <!-- Columna izquierda: Plan de inscripción + Comprobante -->
            <v-col cols="12" md="6">
              <!-- Tabla de cobros del plan -->
              <v-card outlined class="mb-3">
                <v-card-title class="py-2 blue lighten-5 subtitle-2 blue--text text--darken-3">
                  <v-icon small left color="blue darken-3">mdi-cash-multiple</v-icon>
                  {{ plan_inscripcion.nombre }}
                </v-card-title>
                <v-data-table
                  :headers="headers"
                  :items="plan_inscripcion.cobros"
                  hide-default-footer
                  disable-pagination
                  dense
                  class="elevation-0"
                >
                </v-data-table>
              </v-card>

              <!-- Datos del comprobante de pago -->
              <v-card outlined>
                <v-card-title class="py-2 blue lighten-5 subtitle-2 blue--text text--darken-3">
                  <v-icon small left color="blue darken-3">mdi-receipt</v-icon>
                  Comprobante de Pago (ingresado por el asesor)
                </v-card-title>

                <div v-if="programa.xcomprobante_pago">
                  <v-card-text class="pb-0">
                    <v-row dense>
                      <v-col cols="6">
                        <div class="comprobante-field">
                          <span class="caption grey--text text--darken-1">Monto</span>
                          <div class="body-2 font-weight-bold green--text text--darken-2">
                            {{ programa.xcomprobante_pago.monto }}
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="comprobante-field">
                          <span class="caption grey--text text--darken-1">Nro. Comprobante</span>
                          <div class="body-2 font-weight-bold">{{ programa.xcomprobante_pago.nro_comprobante }}</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="comprobante-field">
                          <span class="caption grey--text text--darken-1">Banco</span>
                          <div class="body-2">{{ programa.xcomprobante_pago.banco }}</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="comprobante-field">
                          <span class="caption grey--text text--darken-1">Número de cuenta</span>
                          <div class="body-2">{{ programa.xcomprobante_pago.nro_cuenta }}</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="comprobante-field">
                          <span class="caption grey--text text--darken-1">Depositante</span>
                          <div class="body-2">{{ programa.xcomprobante_pago.nombre_depositante }}</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="comprobante-field">
                          <span class="caption grey--text text--darken-1">Fecha de pago</span>
                          <div class="body-2 font-weight-bold">{{ programa.xcomprobante_pago.fecha_pago }}</div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>

                  <v-divider></v-divider>

                  <div class="pa-2">
                    <span class="caption grey--text text--darken-1 ml-1">Archivo adjunto</span>
                    <iframe
                      :src="'data:'+programa.xcomprobante_pago.archivo.mimetype+';base64,'+programa.xcomprobante_pago.archivo.file64"
                      frameborder="0"
                      class="comprobante-iframe"
                    ></iframe>
                  </div>
                </div>

                <v-card-text v-else class="text-center grey--text py-6">
                  <v-icon large color="grey lighten-1">mdi-file-document-outline</v-icon>
                  <div class="body-2 mt-2">Sin comprobante adjunto</div>
                </v-card-text>
                
                <v-card-text v-if="programa.xcomprobante_pago" class="pt-2 pb-3 px-3">
                  <v-divider class="mb-3"></v-divider>

                  <!-- Estado actual -->
                  <div class="d-flex align-center mb-3" style="gap:8px">
                    <!-- {{ programa.xcomprobante_pago }} -->
                    <span class="caption grey--text text--darken-1">Estado del comprobante:</span>
                    <v-chip
                      small
                      :color="
                        programa.xcomprobante_pago.estado_comprobante === 'APROBADO' ? 'green darken-1' :
                        programa.xcomprobante_pago.estado_comprobante === 'RECHAZADO' ? 'red darken-1' :
                        'orange darken-1'
                      "
                      dark
                      class="font-weight-bold">
                      <v-icon small left>
                        {{
                          programa.xcomprobante_pago.estado_comprobante === 'APROBADO' ? 'mdi-check-circle' :
                          programa.xcomprobante_pago.estado_comprobante === 'RECHAZADO' ? 'mdi-close-circle' :
                          'mdi-clock-outline'
                        }}
                      </v-icon>
                      {{ programa.xcomprobante_pago.estado_comprobante || 'PENDIENTE' }}
                    </v-chip>
                  </div>
                  <v-alert
                    v-if="programa.xcomprobante_pago.observaciones_rechazo"
                    dense text type="error"
                    border="left"
                    class="mb-3 caption">
                    <strong>Motivo de rechazo:</strong> {{ programa.xcomprobante_pago.observaciones_rechazo }}
                    <v-divider color="red" class="my-2"/>
                    <p class="mb-0">por: {{ programa.xcomprobante_pago.updated_by }}</p>
                    <p class="my-0">el: {{ programa.xcomprobante_pago.updated_at }}</p>
                  </v-alert>

                  <!-- Campo de observación para rechazo -->
                  <v-expand-transition>
                    <div v-if="mostrar_rechazo" class="mb-3">
                      <v-textarea
                        v-model="observacion_rechazo"
                        label="Motivo de rechazo"
                        placeholder="Describa el motivo del rechazo..."
                        outlined dense rows="3" hide-details
                        class="mb-2" />
                      <div class="d-flex" style="gap:8px">
                        <v-btn
                          small rounded color="red darken-1" dark
                          :loading="loading_estado_compro"
                          :disabled="!observacion_rechazo.trim()"
                          @click="confirmarRechazo">
                          <v-icon small left>mdi-close-circle</v-icon>
                          Confirmar rechazo
                        </v-btn>
                        <v-btn small rounded outlined @click="mostrar_rechazo = false; observacion_rechazo = ''">
                          Cancelar
                        </v-btn>
                      </div>
                    </div>
                  </v-expand-transition>

                  <!-- Botones de acción -->
                  <div v-if="!mostrar_rechazo" class="d-flex flex-wrap" style="gap:8px">
                    <v-btn
                      small rounded color="green darken-1" dark
                      :loading="loading_estado_compro"
                      @click="set_estado_comprobante('APROBADO')">
                      <v-icon small left>mdi-check-circle</v-icon>
                      Aprobar
                    </v-btn>
                    <v-btn
                      small rounded color="red darken-1" dark
                      :disabled="loading_estado_compro"
                      @click="mostrar_rechazo = true">
                      <v-icon small left>mdi-close-circle</v-icon>
                      Rechazar
                    </v-btn>
                    <v-btn
                      small rounded color="orange darken-1" dark
                      :loading="loading_estado_compro"
                      @click="set_estado_comprobante('PENDIENTE')">
                      <v-icon small left>mdi-clock-outline</v-icon>
                      Pendiente
                    </v-btn>
                  </div>

                  <!-- PARA EL CCA SE PLANEA QUE SE PUEDA CAMBIAR DE ASESOR, YA QUE AVECES COMO NO ESTA EL ASESOR EN LA OFICINA LO INSCRIBE OTRO ASESOR -->
                  <!-- <div v-if="!mostrar_rechazo" class="d-flex flex-wrap mt-4" style="gap:8px">
                    <v-btn @click="openDialogChangeAsesor()"> Asignar Asesor </v-btn>
                  </div> -->
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Columna derecha: Cuotas del plan de pagos -->
            <v-col cols="12" md="6">
              <v-card outlined>
                <v-card-title class="py-2 blue lighten-5 subtitle-2 blue--text text--darken-3">
                  <v-icon small left color="blue darken-3">mdi-calendar-month</v-icon>
                  Cuotas del Plan de Pagos
                </v-card-title>

                <!-- Leyenda de colores -->
                <v-card-text class="pb-0 pt-2">
                  <v-row dense>
                    <v-col cols="auto">
                      <v-chip x-small color="#fb706d78" class="font-weight-medium caption px-2">
                        Mora +1 mes
                      </v-chip>
                    </v-col>
                    <v-col cols="auto">
                      <v-chip x-small color="#ffe17694" class="font-weight-medium caption px-2">
                        Mora reciente
                      </v-chip>
                    </v-col>
                    <v-col cols="auto">
                      <v-chip x-small color="#0076fb5e" class="font-weight-medium caption px-2">
                        Regularizado
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card-text>

                <v-data-table
                  :headers="headersItems"
                  :items="plan_pagos"
                  hide-default-footer
                  disable-pagination
                  dense
                  class="elevation-0"
                  :item-class="itemRowBackgroundMora"
                >
                  <template v-slot:no-data>
                    <div class="text-center py-6">
                      <v-icon large color="orange lighten-2">mdi-alert-circle-outline</v-icon>
                      <div class="body-2 font-weight-bold orange--text text--darken-2 mt-2">
                        El estudiante no tiene plan de pagos
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </v-card>

              <v-divider></v-divider>
              <div v-if="xdocumento_dni_64">
                <b class="pl-3"> Documento de identidad de la persona, subido por el asesor: </b>
                <div class="px-2">
                  <span class="caption grey--text text--darken-1 ml-1">Archivo adjunto</span>
                  <iframe
                    :src="xdocumento_dni_64"
                    frameborder="0"
                    class="comprobante-iframe"
                  />
                </div>

                <!-- funciona pero no se si va aqui :) -->
                <!-- <v-btn
                  color="red darken-1"
                  class="white--text mr-4"
                  :loading="saving_verificado"
                  :disabled="saving_verificado"
                  @click="guardar_verificado(false)"
                >
                  <v-icon left small>mdi-close-circle-outline</v-icon>
                  No verificado
                </v-btn>
                <v-btn
                  color="teal darken-1"
                  class="white--text"
                  :loading="saving_verificado"
                  :disabled="saving_verificado"
                  @click="guardar_verificado(true)"
                >
                  <v-icon left small>mdi-check-circle-outline</v-icon>
                  Verificado
                </v-btn> -->
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Acciones -->
        <v-card-actions class="pa-3">
          <v-progress-linear height="3" indeterminate color="blue" v-if="verificando_netsuite" />
          <v-spacer></v-spacer>

          <template v-if="plan_pagos.length > 0">
            <v-btn
              v-show="(!verificando_netsuite && comprobar_netsuite) && comrpobante_aprobado"
              color="orange darken-1"
              @click="enviarANetsuite()"
              rounded
              small
              dark
              class="mr-1"
            >
              <v-icon class="mr-1" small left>mdi-send</v-icon>
              Enviar a Netsuite
            </v-btn>

            <v-btn
              v-show="!verificando_netsuite && !comprobar_netsuite"
              v-if="!existRecibo"
              color="info"
              rounded
              small
              @click="generatePlan(true)"
              class="mr-1"
            >
              <v-icon class="mr-1">mdi-sync</v-icon>
              Cambiar de plan
            </v-btn>

            <v-btn
              v-show="(!verificando_netsuite && !comprobar_netsuite) && comrpobante_aprobado"
              color="success"
              @click="aprobarPlan"
              rounded
              small
              class="mr-1"
            >
              <v-icon class="mr-1">mdi-check-circle</v-icon>
              Aprobar Plan de Pagos
            </v-btn>
          </template>

          <template v-else>
            <v-btn
              v-show="!verificando_netsuite && !comprobar_netsuite"
              color="amber darken-2"
              rounded
              small
              dark
              @click="generatePlan(false)"
              class="mr-1"
            >
              <v-icon class="mr-1">mdi-plus-circle</v-icon>
              Generar Plan de Pagos
            </v-btn>
          </template>

          <v-btn color="grey darken-1" outlined rounded small @click="cancel">
            <v-icon left small>mdi-close</v-icon>
            Cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <MandarNetSuite ref="refMandarNetsuite" v-on:success="successNetSuite" />
    <change-asesor ref="refChangeAsesor" @updateIns="updateInscripcion" />
  </v-row>
</template>

<script>
import xaxios from '@/xaxios';
import MandarNetSuite from '@/components/Programa-insNetsuite.vue';
import gql from 'graphql-tag';
import ChangeAsesor from '@/views/programas/ChangeAsesor.vue';

export default {
  props: ['programa', 'existRecibo'],
  components:{ MandarNetSuite,ChangeAsesor },
  data() {
    return {
      dialog: false,
      api_rest: process.env.VUE_APP_API_REST,
      plan_inscripcion: null,
      plan_pagos: [],
      verificando_netsuite: false,
      loading_estado_compro: false,
      comrpobante_aprobado: true, // para contactos mkt poner en false
      comprobar_netsuite: null,
      mostrar_rechazo: false,
      observacion_rechazo: '',

      headers: [
        { text: 'Nombre', value: 'nombre' },
        { text: 'Monto', value: 'monto' },
        { text: 'Cuotas', value: 'cuotas' },
      ],
      headersItems: [
        { text: 'Concepto de pago', value: 'concepto' },
        { text: 'N° de Cuota', value: 'numero_cuota' },
        { text: 'Monto', value: 'monto' },
        { text: 'Saldo', value: 'saldo' },
        { text: 'Fecha Pago', value: 'date_pago' },
      ],
      thiz_padre: {},
      xdocumento_dni_64: '',
      saving_verificado: false,
    };
  },
  watch: {
    dialog(val) {
      val || this.closeDialog();
    },
  },
  methods: {
    showThisView(plan_inscripcion, plan_pagos = [],thiz_padre={}) {
      this.xdocumento_dni_64 = ''
      this.dialog = true;
      this.plan_inscripcion = plan_inscripcion;
      this.plan_pagos = plan_pagos.sort((a, b) => a.id - b.id);
      this.thiz_padre = thiz_padre;

      this.iniciarForNetSuite();
      window.console.log('AprobarPlanIns.vue', this.programa,thiz_padre,plan_inscripcion);
      this.jalar_dni_persona();
    },
    aprobarPlan() {
      const url = `${this.api_rest}/api/esam/inscripcion/plan/aprobar`;
      xaxios
        .post(url, { inscripcion_id: this.programa.id, user_id: this.$store.state.DATA_USER.id })
        .then(() => {
          this.closeDialog();
          this.$emit('clickedAprobe', { estado_plan_id: true, aprobado_plan_user_id: this.$store.state.DATA_USER.id });
        })
        .catch(({ response: { data } }) => this.$root.$notify.show(`${data.error}`, { color: 'danger' }));
    },
    cancel() {
      this.$root.$notify.show(`${this.plan_inscripcion.nombre}, No fue aprobado para esta inscripcion, por tanto nose puede emitir recibo`, {
        color: 'red',
        timeout: 5000,
      });
      this.closeDialog();
    },
    closeDialog() {
      this.dialog = false;
      this.plan_inscripcion = null;
      this.plan_pagos = [];
    },
    generatePlan(value) {
      if(value){
        const idsede = this.$store.state.CARGO.sede.id
        const contiene_avd = this.programa.nombre_compuesto.includes('U301 AVANZADO)')
        window.console.log('contiene_avd', contiene_avd);

        if(idsede == 82 && contiene_avd ){
          const u = `/generar_plan_upi/${this.programa.programa_id}?ins_id=${this.programa.id}&asesor_id=${this.programa.asesor_id}&doc=${this.thiz_padre.persona.num_doc}&cambio_plan=true`;
          this.$router.push(u);
          return;
        }
      }

      this.$store.dispatch('inscripcion/selectInscripcion', {id:this.programa.id});
      let programa = Object.assign(
        {
          programa: { id: this.programa.programa_id },
          estudiante: { id: this.programa.estudiante_id },
          asesor: { id: this.programa.asesor_id },
        },
        this.programa
      );

      window.sessionStorage.setItem('xinscritotoplan', JSON.stringify(programa));
      window.sessionStorage.setItem(
        'xprograma',
        JSON.stringify({ id: programa.programa_id, codigo: programa.nombre_compuesto, inscripcion_id: this.programa.id, plan_pagos: this.plan_pagos, estudiante_id:this.programa.estudiante_id })
      );
      value ? this.$router.push({ name: 'inscripcionrest' }) : this.$router.push({ name: 'regularizar' });
    },
    eliminarPlan() {
      const url = `${this.api_rest}/api/esam/inscripcion/plan/eliminar`;
      xaxios
        .post(url, { inscripcion_id: this.programa.id })
        .then(() => {
          this.plan_pagos = [];
          this.$emit('deletePlan', true);
          this.$root.$notify.show(`${'Plan de Pagos  Eliminado del estudiante. Debe generar uno nuevo'}`, { color: 'info' });
        })
        .catch(({ response: { data } }) => this.$root.$notify.show(`${data.error}`, { color: 'danger' }));
    },
    itemRowBackgroundMora(item) {
      let fecha_pago = new Date(item.fecha_pago);
      fecha_pago.setDate(fecha_pago.getDate() + 1);

      let fecha_mes = new Date(item.fecha_pago);
      fecha_mes.setMonth(fecha_mes.getMonth() + 1);

      if (fecha_mes < new Date() && item.saldo != 0) return 'mora-mes-1';
      if (fecha_pago < new Date() && item.saldo != 0) return 'mora-semana-1';
      if (item.saldo_regularizado) return 'regularizado';
      const idx = this.plan_pagos.indexOf(item);
      return idx % 2 === 0 ? 'grupo-par' : 'grupo-impar';
    },
    async iniciarForNetSuite(){
      let unidad = this.$store.state.CARGO.sede.unidad_negocio;
      if( unidad == 6){
        this.comprobar_netsuite = null;
        return;
      }
      this.verificando_netsuite = true;
      this.comprobar_netsuite = null;

      const url2 = `${this.api_rest}/esam/persona/id/${this.thiz_padre.persona.id}`;
      let res2 = await xaxios.get(url2).then(({data})=>data).catch(e=>e);
      this.thiz_padre.zestudent = res2.persona;
      this.thiz_padre.zestudent.pri_apellido = res2.persona.primer_apellido;
      this.thiz_padre.zestudent.seg_apellido = res2.persona.segundo_apellido;
      this.thiz_padre.zestudent.num_doc = res2.persona.numero_documento;
      this.thiz_padre.zestudent.email = res2.persona.email;
      this.verificando_netsuite = false;

      let sede = this.$store.state.CARGO.sede;
      window.console.log('res:::>', this.thiz_padre,res2,sede);
      if( !res2.persona.cliente_netsuite_id && sede.subsidiaria_id){
        this.comprobar_netsuite = {
          subsidiaria: sede.subsidiaria,
          subsidiaria_id: sede.subsidiaria_id,
          ubicacion: sede.ubicacion,
          ubicacion_id: sede.ubicacion_id,
        }
      }
      if(!sede.subsidiaria_id){
        this.$root.$notify.show('La sede no tiene subsidiaria netsuite', {color:'red',x: 'right',});
      }
      this.comprobando_netsuite = res2.persona.cliente_netsuite_id != undefined;
    },
    enviarANetsuite(){
      let idsedeprog = this.thiz_padre.programa.sede_id;
      let sede = this.$store.state.CARGOS.find(c=>c.idsede==idsedeprog);
      const customdata = {
        estudiante_id: this.thiz_padre.programa.estudiante_id,
        postragrado_id: this.thiz_padre.programa.posgrado_id,
        sede: {...sede.sede}
      }
      this.$refs.refMandarNetsuite.openInsNet(this.thiz_padre.zestudent,customdata,false);
    },
    successNetSuite(res){
      if(res == 'success'){
        this.comprobar_netsuite = null;
      }
    },

    confirmarRechazo() {
      this.set_estado_comprobante('RECHAZADO', this.observacion_rechazo);
      this.mostrar_rechazo = false;
      this.observacion_rechazo = '';
    },
    async set_estado_comprobante(estado, observacion=null){
      const datas = {
        comprobante_pago_id: this.programa.xcomprobante_pago.id,
        estado: estado
      }
      if(observacion) datas.observaciones_rechazo = observacion;
      
      this.loading_estado_compro = true
      const url = `${process.env.VUE_APP_API_REST}/api/esam/comprobante-pago/estado`;
      const res = await xaxios.patch(url, datas).then(({data}) =>data).catch(e=>e)
      this.loading_estado_compro = false  
      this.comrpobante_aprobado = false 

      if( res && res.data){
        const exito = res.data.includes('actualizacion de comprobante realizada')
        if(exito && estado == 'APROBADO') {
          this.programa.xcomprobante_pago.estado_comprobante = 'APROBADO'
          this.programa.xcomprobante_pago.observaciones_rechazo = ''
          this.comrpobante_aprobado = true;
          this.$root.$notify.show('comprobante aprobado, puede continuar.', { color: 'green' })
        }
        else if(exito && estado == 'RECHAZADO'){
          this.programa.xcomprobante_pago.estado_comprobante = 'RECHAZADO'
          this.programa.xcomprobante_pago.observaciones_rechazo = observacion
          this.$root.$notify.show('El comprobante se ha rechazado, no puede aprobar este plan', { color: 'red' })
        }else{
          this.programa.xcomprobante_pago.estado_comprobante = 'PENDIENTE'
          this.programa.xcomprobante_pago.observaciones_rechazo = ''
        }
      }
    },

    async jalar_dni_persona(){
      const uuid = this.programa.xdocumento_persona?.documento_identidad
      const SERVER_FILES = process.env.VUE_APP_SERVER_FILE;
      const url = SERVER_FILES + uuid;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        }, 
      });

      const data = await response.json();

      // equivalente a ResponseDownFile
      const mimeType = data.mimetype;
      const file64 = data.file64;

      // base64:data:image/jpeg;base64,...
      const base64 = `data:${mimeType};base64,${file64}`; 
      this.xdocumento_dni_64 = base64;
    },
    async guardar_verificado(is_verificado) {
      this.saving_verificado = true; 
      const userid = this.programa.estudiante_id;
      const sql = gql`mutation{ updateIsVerificado(id:${userid}, is_verificado:${is_verificado}){ ok } }`;
      try {
        const { data } = await this.$apollo.mutate({ mutation: sql });
        const res = data && data.updateIsVerificado ? data.updateIsVerificado : null;
        if (res && res.ok) {
          const msg = is_verificado ? 'Persona marcada como verificada' : 'Persona marcada como no verificada';
          this.$root.$notify.show(msg, { color: is_verificado ? 'teal' : 'orange', x: 'right', y:'bottom' });
        } else {
          this.$root.$notify.show(res ? res.message : 'No se pudo guardar', { color: 'red', x: 'right' });
        }
      } catch (e) {
        this.$root.$notify.show('Error al guardar la verificación', { color: 'red', x: 'right' });
      } finally {
        this.saving_verificado = false; 
      }
    },

    openDialogChangeAsesor() { 
      const inscrito = {
        asesor: {
          id: this.programa.asesor_id,
        }
      }
      if (this.$store.state.CARGO.id == 1 /* || this.$store.state.CARGO.id == 8 */) {
        this.$refs.refChangeAsesor.showThisView(inscrito);
      }
    },
    
  },
};
</script>

<style scoped>
.comprobante-field {
  margin-bottom: 8px;
}
.comprobante-iframe {
  width: 100%;
  height: 480px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-top: 4px;
}
</style>
<style>
.mora-mes-1 td {
  background-color: #fb706d78 !important;
}
.mora-semana-1 td {
  background-color: #ffe17694 !important;
}
.regularizado td {
  background-color: #0076fb5e !important;
}
.grupo-par td {
  background-color: #fcfcfc !important;
}
.grupo-impar td {
  background-color: #f1f5ff !important;
}
</style>
